import * as React from 'react';

import { ILinkCreationRequestParameter, IFieldSwitcherProps } from '../types';

import {
  FormInput,
  FormSelect,
  FormCheckbox,
  FormDateInput,
} from 'src/components';
import AddField from './AddField';
import { IOption, IOptionGroup } from 'src/components/Form/Select';
import { InformationTooltips } from './InformationTooltips';

function formatFieldName(item: ILinkCreationRequestParameter) {
  return `${item.name}${item.mandatory ? '*' : ''}`;
}

const HintMessage = ({ item }: { item: ILinkCreationRequestParameter }) =>
  item.showHelpMessagePermanently ? (
    <p className="mt-1 pt-1 text-xs italic">{item.helpMessage}</p>
  ) : null;

const FieldSwitcher = ({
  name,
  item,
  onAdd,
  isTargetChecking,
  targetCheckResult,
}: IFieldSwitcherProps) => {
  if (!item.render) {
    return null;
  }
  switch (item.type) {
    case 'TextBox':
    case 'TextBoxAuto':
    case 'FreeText':
    case 'Autocomplete':
      return (
        <div className="fade-in w-1/2 pr-16 flex mb-4">
          <label className="w-48 pt-2 pr-6" htmlFor={`${name}.valueText`}>
            {formatFieldName(item)}
          </label>
          <div className="flex-1">
            <div className="flex items-center">
              <FormInput
                className="flex-1"
                name={`${name}.valueText`}
                required={item.mandatory}
              />
              <InformationTooltips
                item={item}
                isTargetChecking={isTargetChecking}
                targetCheckResult={targetCheckResult}
              />
            </div>
            <HintMessage item={item} />
          </div>
        </div>
      );

    case 'RadioButton':
      return (
        <div className="fade-in w-1/2 pr-16 flex py-2">
          <span className="w-48 pr-6">{formatFieldName(item)}</span>
          <div className="flex-1">
            <div className="flex items-start">
              <div className="flex-1 flex flex-wrap">
                {item.items.map((el, index) => (
                  <FormCheckbox
                    key={el.id}
                    type="radio"
                    name={`${name}.selectedItemId`}
                    id={`${name}.selectedItemId-${index}`}
                    value={el.id}
                    className="mr-8 mb-4"
                    required={item.mandatory}
                  >
                    {el.name}
                  </FormCheckbox>
                ))}
              </div>
              <InformationTooltips item={item} />
            </div>
            <HintMessage item={item} />
            {item.canAdd && (
              <AddField id={item.id} regex={item.validation} onAdd={onAdd} />
            )}
          </div>
        </div>
      );

    case 'DropDownList':
      const groups = Object.entries(
        item.items.reduce((out, el) => {
          const element: IOption<number> = {
            value: el.id,
            label: el.name,
          };
          if (el.groupname === null) {
            if (!out.Other) {
              out.Other = [];
            }
            out.Other.push(element);
          } else {
            if (!out[el.groupname]) {
              out[el.groupname] = [];
            }
            out[el.groupname].push(element);
          }
          return out;
        }, {})
      ).map(([groupname, items]) => ({
        label: groupname,
        options: items,
      })) as Array<IOptionGroup<number>>;

      const placeholderValue = {
        label: '',
        value: '',
      };

      return (
        <div className="fade-in w-1/2 pr-16 flex mb-4">
          <span className="w-48 pt-2 pr-6">{formatFieldName(item)}</span>
          <div className="flex-1">
            <div className="flex items-center">
              <FormSelect
                className="w-full"
                name={`${name}.selectedItemId`}
                options={item.items.map((el) => ({
                  value: el.id,
                  label: el.name,
                }))}
                isClearable={true}
                groups={groups.length > 1 ? groups : undefined}
                initialValue={placeholderValue}
              />
              <InformationTooltips item={item} />
            </div>
            <HintMessage item={item} />
            {item.canAdd && (
              <AddField id={item.id} regex={item.validation} onAdd={onAdd} />
            )}
          </div>
        </div>
      );

    case 'DateBox':
      return (
        <div className="fade-in w-1/2 pr-16 flex mb-4">
          <span className="w-48 pt-2 pr-6">{formatFieldName(item)}</span>
          <div className="flex-1 flex items-center">
            <div className="flex-1">
              <FormDateInput
                className="w-full"
                name={`${name}.valueText`}
                required={item.mandatory}
                formatDateValue={true}
              />
              <HintMessage item={item} />
            </div>
            <InformationTooltips item={item} />
          </div>
        </div>
      );

    default:
      console.warn('Unknown field type:', item.type);
      return null;
  }
};

export default FieldSwitcher;



// WEBPACK FOOTER //
// ./src/views/LinkCreate/components/FieldSwitcher.tsx